<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="show && !isMinimized"
        class="incoming-call-overlay"
        :class="{ 'dragging-active': !isInitialPosition }"
      >
        <div
          ref="modalRef"
          class="incoming-call-modal"
          :class="{ 'is-dragging': isDragging }"
          :style="modalStyle"
          @click.stop
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <div class="call-header" :class="{ draggable: true }">
            <div class="header-left">
              <Icon icon="mdi:phone" class="phone-icon" />
              <span class="status-dot"></span>
              <h6 class="header-title">
                {{ callState === "active" ? "Active Call" : "Incoming Call" }}
              </h6>
            </div>
            <div class="header-right">
              <button
                type="button"
                class="btn-minimize"
                @click="handleMinimize"
                title="Minimize"
                aria-label="Minimize call"
              >
                <Icon icon="mdi:minus" width="16" height="16" />
              </button>
              <div class="drag-handle" title="Drag to move">
                <Icon icon="mdi:drag" width="16" height="16" />
              </div>
              <button type="button" class="btn-close" @click="handleClose" aria-label="Close">
                <Icon icon="mdi:close" width="18" height="18" />
              </button>
            </div>
          </div>

          <div class="caller-info-section">
            <div class="avatar-container">
              <div class="pulse-ring pulse-ring-1"></div>
              <div class="pulse-ring pulse-ring-2"></div>
              <div class="avatar-circle">
                <span class="avatar-initials">{{ callerInitials }}</span>
              </div>
            </div>

            <h3 v-if="callerName" class="caller-name">{{ callerName }}</h3>
            <p class="caller-phone">{{ formattedPhoneNumber }}</p>
            <p v-if="callerCompany" class="caller-company">{{ callerCompany }}</p>
            <p v-if="callState === 'ringing'" class="call-status">Ringing...</p>
            <p v-else-if="callState === 'active'" class="call-timer">{{ formattedCallDuration }}</p>
            <button
              v-if="contactId"
              class="view-contact-btn"
              @click="handleViewContact"
              aria-label="View contact"
            >
              <Icon icon="mdi:account-details-outline" width="16" height="16" />
              View Contact
            </button>
          </div>

          <!-- Live Caption -->
          <div
            v-if="callState === 'active' && enableTranscription && callSid"
            class="transcription-section"
          >
            <LiveCaption
              :is-active="callState === 'active'"
              :enabled="enableTranscription"
              :call-sid="callSid"
              ref="liveCaptionRef"
            />
          </div>

          <div class="call-actions">
            <template v-if="callState === 'ringing'">
              <div class="action-group">
                <button
                  class="action-btn decline-btn"
                  @click="handleDecline"
                  aria-label="Decline call"
                >
                  <Icon icon="mdi:phone-hangup" width="20" height="20" />
                </button>
                <span class="action-label">Decline</span>
              </div>

              <div class="action-group">
                <button
                  class="action-btn answer-btn"
                  @click="handleAnswer"
                  aria-label="Answer call"
                >
                  <Icon icon="mdi:phone" width="20" height="20" />
                </button>
                <span class="action-label">Answer</span>
              </div>
            </template>

            <template v-else-if="callState === 'active'">
              <div class="action-group hangup-group">
                <button
                  class="action-btn hangup-btn"
                  @click="handleHangup"
                  aria-label="Hangup call"
                >
                  <Icon icon="mdi:phone-hangup" width="20" height="20" />
                </button>
                <span class="action-label">Hangup</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount, onMounted } from "vue";
import { Icon } from "@iconify/vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import LiveCaption from "@/components/call/LiveCaption.vue";

interface Props {
  show: boolean;
  callerName?: string;
  callerPhoneNumber?: string;
  callerCompany?: string;
  callSid?: string;
  contactId?: string;
  callState?: "ringing" | "active" | null;
  callDuration?: number;
  isMinimized?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  callerName: "",
  callerPhoneNumber: "",
  callerCompany: "",
  callSid: "",
  contactId: "",
  callState: null,
  callDuration: 0,
  isMinimized: false,
});

const emit = defineEmits<{
  answer: [];
  decline: [];
  hangup: [];
  close: [];
  minimize: [];
}>();

const router = useRouter();
const store = useStore();

// Transcription state from account settings
const account = computed(() => store.getters.account.data);
const enableTranscription = computed(() => account.value?.enableTranscription !== false);
const enableAISummary = computed(
  () => enableTranscription.value && account.value?.enableAISummary !== false
);
const liveCaptionRef = ref<InstanceType<typeof LiveCaption> | null>(null);

// Expose transcription messages for minimized view
const getTranscriptionMessages = () => {
  return liveCaptionRef.value?.displayedMessages || [];
};

defineExpose({
  getTranscriptionMessages,
  enableTranscription,
  enableAISummary,
});

const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const audioRef = ref<HTMLAudioElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};

  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: "360px",
    maxWidth: "450px",
    borderRadius: "16px !important",
    zIndex: "10000",
  };
});

const callerInitials = computed(() => {
  if (props.callerName) {
    const names = props.callerName.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
  if (props.callerPhoneNumber) {
    const digits = props.callerPhoneNumber.replace(/\D/g, "");
    return digits.slice(-2) || "??";
  }
  return "??";
});

const formattedPhoneNumber = computed(() => {
  if (!props.callerPhoneNumber) return "Unknown Number";
  const cleaned = props.callerPhoneNumber.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return props.callerPhoneNumber;
});

const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close",
    ".btn-minimize",
    ".action-btn",
    ".action-label",
    ".call-actions",
    ".caller-info-section",
    ".transcription-section",
    ".transcription-toggle",
    ".toggle-item",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 360) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;

  dragEvents.forEach(([event, handler, options]) => {
    document.addEventListener(event, handler as any, options);
  });
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;

  const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
  const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

  dragEvents.forEach((event, index) => {
    document.removeEventListener(event, handlers[index] as any);
  });
};

// Audio functions
const RINGING_SOUND_URL = new URL("../../assets/audio/ringing-new.m4a", import.meta.url).href;

const initAudio = () => {
  if (audioRef.value) return;

  try {
    audioRef.value = new Audio(RINGING_SOUND_URL);
    audioRef.value.loop = true;
    audioRef.value.volume = 0.7;
  } catch (error) {
    console.error("Failed to initialize audio:", error);
  }
};

const playRingingSound = async () => {
  if (!audioRef.value) {
    initAudio();
  }

  if (audioRef.value) {
    try {
      await audioRef.value.play();
    } catch (error) {
      console.warn("Audio playback failed (may require user interaction):", error);
    }
  }
};

const stopRingingSound = () => {
  if (audioRef.value) {
    audioRef.value.pause();
    audioRef.value.currentTime = 0;
  }
};

const formattedCallDuration = computed(() => {
  if (props.callDuration > 0) {
    const hours = Math.floor(props.callDuration / 3600);
    const minutes = Math.floor((props.callDuration % 3600) / 60);
    const seconds = props.callDuration % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return "00:00:00";
});

const handleAnswer = () => {
  stopRingingSound();
  emit("answer");
};

const handleHangup = () => {
  stopRingingSound();
  resetPosition();
  emit("hangup");
};

const handleDecline = () => {
  stopRingingSound();
  resetPosition();
  emit("decline");
  emit("close");
};

const handleClose = () => {
  stopRingingSound();
  resetPosition();
  emit("close");
};

const handleMinimize = () => {
  emit("minimize");
};

const handleViewContact = () => {
  if (props.contactId) {
    router.push({
      name: CONTACT_OVERVIEW_ROUTE,
      params: { id: props.contactId },
    });
  }
};

const resetPosition = () => {
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.show) return;

  if ((event.key === "Enter" || event.key === " ") && props.callState === "ringing") {
    event.preventDefault();
    handleAnswer();
  } else if (event.key === "Escape" && props.callState === "ringing") {
    event.preventDefault();
    handleDecline();
  }
};

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      playRingingSound();
      document.addEventListener("keydown", handleKeydown);
    } else {
      stopRingingSound();
      document.removeEventListener("keydown", handleKeydown);
      resetPosition();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopRingingSound();
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
  document.removeEventListener("keydown", handleKeydown);

  if (audioRef.value) {
    audioRef.value = null;
  }
});
</script>

<style scoped>
.incoming-call-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  pointer-events: none;
}

.incoming-call-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-start;
}

.incoming-call-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 360px;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
  position: relative;
  overflow: hidden;
}

.incoming-call-modal.is-dragging {
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  cursor: grabbing;
  transition: none;
}

.call-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  transition: background-color 0.2s ease;
}

.call-header.draggable {
  cursor: grab;
  user-select: none;
}

.call-header.draggable:hover {
  background-color: #f8f9fa;
}

.call-header.draggable:active {
  cursor: grabbing;
  background-color: #e9ecef;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.phone-icon {
  font-size: 18px;
  color: #3e3efe;
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: #6b7280;
  cursor: grab;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.drag-handle:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.drag-handle:active {
  cursor: grabbing;
}

.btn-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.caller-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;
  text-align: center;
}

.avatar-container {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 120px;
  height: 120px;
  border: 2px solid #9333ea;
  border-radius: 50%;
  opacity: 0;
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.pulse-ring-1 {
  animation-delay: 0s;
}

.pulse-ring-2 {
  animation-delay: 1s;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

.avatar-circle {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
}

.avatar-initials {
  font-size: 36px;
  font-weight: 600;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.caller-name {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
}

.caller-phone {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.caller-company {
  font-size: 12px;
  color: #9ca3af;
  margin: 0 0 12px 0;
}

.call-status {
  font-size: 14px;
  font-weight: 500;
  color: #3e3efe;
  margin: 12px 0 0 0;
}

.call-timer {
  font-size: 32px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #111827;
  margin: 12px 0 0 0;
}

.view-contact-btn {
  margin-top: 16px;
  padding: 8px 16px;
  background: #3e3efe;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.view-contact-btn:hover {
  background: #2d2dd4;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(62, 62, 254, 0.3);
}

.view-contact-btn:active {
  transform: translateY(0);
}

.call-actions {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 24px;
  border-top: 1px solid #e8e8e8;
  background: #f9fafb;
}

.action-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.action-btn {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.action-btn:active {
  transform: scale(0.95);
}

.decline-btn {
  background: #ef4444;
  color: #ffffff;
}

.decline-btn:hover {
  background: #dc2626;
}

.answer-btn {
  background: #10b981;
  color: #ffffff;
}

.answer-btn:hover {
  background: #059669;
}

.action-label {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

.hangup-group {
  width: 100%;
  display: flex;
  justify-content: center;
}

.hangup-btn {
  background: #ef4444;
  color: #ffffff;
}

.hangup-btn:hover {
  background: #dc2626;
}

.btn-minimize {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-minimize:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.transcription-section {
  padding: 12px 16px;
  border-top: 1px solid #e8e8e8;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .incoming-call-modal,
.modal-fade-leave-active .incoming-call-modal {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.modal-fade-enter-from .incoming-call-modal,
.modal-fade-leave-to .incoming-call-modal {
  transform: scale(0.9);
  opacity: 0;
}
</style>
